/**
 * DnsDomainForm — ficha de un dominio a PANTALLA COMPLETA (estándar de fichas).
 *
 * Antes el detalle y los registros vivían en un `Dialog` y por eso se veía como
 * un modal con una tabla suelta. Aquí se replica el patrón del resto del CMDB
 * (`FormCardsShell` + `FormCardsHead` + `.dv-tab-pane.sf2-pane`):
 *
 *   · General  — identificación, renovación (caducidad + días) y notas.
 *   · Registros — agrupados por tipo (A, AAAA, CNAME, MX…), con alta/edición.
 *
 * Dos reglas de negocio que se cumplen aquí:
 *   1. **Al entrar siempre se sincroniza con OVH**: lo que se pinta es la zona
 *      real, no la última copia. Si falla, se enseña el motivo y se sigue con
 *      lo que hay en la CMDB.
 *   2. **Alta de dominio**: se comprueba disponibilidad y precio (simulación de
 *      OVH, `dryRun`) y se avisa del coste antes de crear el pedido real. El
 *      pago se completa en OVH; esta pantalla solo abre esa URL.
 *
 * En los registros, OVH no admite ALIAS (se oculta el tipo con proveedor) y si
 * rechaza el cambio el backend hace rollback: aquí se recarga y se enseña el
 * error, sin dejar la sensación de que «se guardó».
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  CheckIcon,
  CloudArrowUpIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import FormCardsShell from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import FormDirtyDock from './common/FormDirtyDock';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';
import Dialog from './common/Dialog';
import { getServerFormStyle } from '../utils/serverFormStyle';

const TIPOS = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'NS', 'SRV', 'CAA', 'PTR', 'ALIAS'];

const ESTADOS = [
  { value: 'active', label: 'Activo' },
  { value: 'expired', label: 'Caducado' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'unknown', label: 'Desconocido' },
  { value: 'transferred', label: 'Transferido' },
];

const DURACIONES = [
  { value: 'P1Y', label: '1 año' },
  { value: 'P2Y', label: '2 años' },
  { value: 'P3Y', label: '3 años' },
  { value: 'P5Y', label: '5 años' },
];

const FORM_VACIO = {
  id: null,
  name: '',
  zone: '',
  provider_id: '',
  status: 'unknown',
  registrar: '',
  expires_at: '',
  auto_renew: true,
  notes: '',
  last_sync_at: null,
  last_sync_message: '',
  records_synced: 0,
};

const REGISTRO_VACIO = {
  id: null,
  record_type: 'A',
  subdomain: '@',
  target: '',
  ttl: 3600,
  priority: '',
};

const PLACEHOLDER_DESTINO = {
  A: '217.76.128.34',
  AAAA: '2001:db8::1',
  CNAME: 'destino.com',
  MX: 'mx1.tudominio.com',
  TXT: '«v=spf1 include:… -all»',
  NS: 'ns1.tudominio.com',
  SRV: 'servicio.dominio.com',
  CAA: '0 issue "letsencrypt.org"',
  PTR: 'host.dominio.com',
  ALIAS: 'destino.com',
};

const RE_IPV4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
const RE_HOST = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*\.?$/i;
const RE_SUB = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/i;
const RE_SUB_EXTRA = /^[a-z0-9_]([a-z0-9_-]{0,61}[a-z0-9_])?(\.[a-z0-9_]([a-z0-9_-]{0,61}[a-z0-9_])?)*$/i;

/**
 * Validación previa del registro, con mensajes que dicen qué se espera.
 * OVH contesta «A field for la-zona is invalid» y no ayuda nada; esto lo
 * evita antes de gastar la llamada (el backend valida lo mismo por si acaso).
 */
function validarRegistro({ record_type, subdomain, target }) {
  const tipo = (record_type || 'A').toUpperCase();
  const destino = (target || '').trim();
  if (!destino) return 'El destino del registro es obligatorio';

  let nombre = (subdomain || '').trim();
  if (nombre.startsWith('*.')) nombre = nombre.slice(2);
  else if (nombre === '*') nombre = '';
  if (nombre && nombre !== '@') {
    const extra = ['TXT', 'SRV', 'MX', 'CAA'].includes(tipo);
    if (!(extra ? RE_SUB_EXTRA : RE_SUB).test(nombre)) {
      return nombre.includes('_') && !extra
        ? `Un registro ${tipo} no admite guiones bajos en el nombre (ej. www)`
        : `El nombre «${nombre}» no es válido para un registro ${tipo}`;
    }
  }

  let host = destino;
  if (tipo === 'MX') {
    const partes = destino.split(/\s+/);
    if (partes.length === 2 && /^\d+$/.test(partes[0])) host = partes[1];
  } else if (tipo === 'SRV') {
    const partes = destino.split(/\s+/);
    if (partes.length >= 4 && partes.slice(0, 3).every((p) => /^\d+$/.test(p))) {
      host = partes[partes.length - 1];
    }
  }

  if (tipo === 'A' && !RE_IPV4.test(host)) {
    return `Un registro A necesita una IPv4 en Destino (ej. 217.76.128.34); «${destino}» no lo es`;
  }
  if (tipo === 'AAAA' && (!host.includes(':') || !/^[0-9a-f:.]+$/i.test(host))) {
    return `Un registro AAAA necesita una IPv6 válida en Destino; «${destino}» no lo es`;
  }
  if (['CNAME', 'NS', 'PTR', 'MX', 'ALIAS'].includes(tipo) && !RE_HOST.test(host)) {
    return `Un registro ${tipo} necesita un nombre de dominio en Destino (ej. destino.com); «${destino}» no lo es`;
  }
  return '';
}

/** Días que quedan hasta la fecha (negativo = vencido) y su tono. */
function vencimiento(fecha) {
  if (!fecha) return null;
  const dias = Math.ceil((new Date(`${fecha}T23:59:59`) - Date.now()) / 86400000);
  return { dias, tono: dias < 0 ? 'bad' : dias <= 30 ? 'warn' : 'ok' };
}

const estadoLabel = (valor) => ESTADOS.find((e) => e.value === valor)?.label || valor || '—';
const estadoTono = (valor) => ({
  active: 'ok',
  expired: 'bad',
  pending: 'warn',
}[valor] || null);

export default function DnsDomainForm({
  domain,
  mode = 'view',
  companyId,
  providers = [],
  superadmin = false,
  siblings = [],
  onClose,
  onSaved,
  onDeleted,
  onNavigate,
}) {
  const isCards = getServerFormStyle() === 'cards';
  const empresaValida = Boolean(companyId) && companyId !== 'all';
  const sinIdInicial = mode === 'create' || !domain?.id;

  const [form, setForm] = useState(() => ({ ...FORM_VACIO, ...(domain || {}) }));
  const [base, setBase] = useState(() => ({ ...FORM_VACIO, ...(domain || {}) }));
  const [editing, setEditing] = useState(sinIdInicial);
  const [tab, setTab] = useState('general');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');

  const [records, setRecords] = useState([]);
  const [cargando, setCargando] = useState(!sinIdInicial);
  const [sincronizando, setSincronizando] = useState(false);

  const [registro, setRegistro] = useState(null);
  const [guardandoRegistro, setGuardandoRegistro] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const editorNuevoRef = useRef(null);

  const [confirmDelete, setConfirmDelete] = useState(false);

  // Alta: disponibilidad + precio + pedido.
  const [duracion, setDuracion] = useState('P1Y');
  const [quote, setQuote] = useState({ busy: false, result: null, error: '' });
  const [registering, setRegistering] = useState(false);
  const [confirmRegister, setConfirmRegister] = useState(false);
  const [order, setOrder] = useState(null);

  const sf = (campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor }));
  // Sin id = alta. En cuanto se registra o se guarda, pasa a ser una ficha normal.
  const esAlta = !form.id;

  const instantanea = (obj) => JSON.stringify({
    name: obj.name || '',
    zone: obj.zone || '',
    provider_id: String(obj.provider_id || ''),
    status: obj.status || '',
    registrar: obj.registrar || '',
    expires_at: obj.expires_at || '',
    auto_renew: Boolean(obj.auto_renew),
    notes: obj.notes || '',
  });
  const dirty = instantanea(form) !== instantanea(base);

  const cargarRegistros = useCallback(async (domainId) => {
    const filas = await api.getDnsRecords(domainId, companyId);
    setRecords(filas || []);
  }, [companyId]);

  /** Trae la zona real de OVH y refleja el resultado en la ficha. */
  const sincronizar = useCallback(async (silencioso = false) => {
    if (!form.id) return;
    if (!form.provider_id) {
      if (!silencioso) setAviso('Este dominio no tiene proveedor: no hay zona que traer.');
      return;
    }
    setSincronizando(true);
    if (!silencioso) setError('');
    try {
      const res = await api.syncDnsDomain(form.id, companyId);
      const detalle = res.data || {};
      const limpio = { ...detalle };
      delete limpio.records;
      setForm((prev) => ({ ...prev, ...limpio }));
      setBase((prev) => ({ ...prev, ...limpio }));
      if (Array.isArray(detalle.records)) setRecords(detalle.records);
      else await cargarRegistros(form.id);
      setAviso(detalle.last_sync_message || 'Zona sincronizada');
    } catch (err) {
      setError(err.message || 'No se pudo sincronizar con OVH');
    } finally {
      setSincronizando(false);
    }
  }, [form.id, form.provider_id, companyId, cargarRegistros]);

  // Al entrar (o al pasar a otro dominio con el paginador) se recarga el
  // detalle y se fuerza la sincronización con OVH: la ficha enseña la realidad.
  useEffect(() => {
    if (!domain?.id) {
      setForm({ ...FORM_VACIO, ...(domain || {}) });
      setBase({ ...FORM_VACIO, ...(domain || {}) });
      setRecords([]);
      setEditing(mode !== 'view');
      return undefined;
    }
    let cancelado = false;
    setCargando(true);
    setError('');
    setAviso('');
    setRegistro(null);
    setOrder(null);
    (async () => {
      let detalle = null;
      try {
        detalle = await api.getDnsDomain(domain.id, companyId);
        if (cancelado) return;
        const limpio = { ...detalle };
        delete limpio.records;
        setForm({ ...FORM_VACIO, ...limpio });
        setBase({ ...FORM_VACIO, ...limpio });
        setEditing(mode !== 'view');
        if (Array.isArray(detalle.records)) setRecords(detalle.records);
        else await cargarRegistros(domain.id);
      } catch (err) {
        if (!cancelado) setError(err.message || 'No se pudo cargar el dominio');
      } finally {
        if (!cancelado) setCargando(false);
      }

      // Datos vivos: al abrir la ficha se lee la zona real de OVH. Si falla,
      // se queda lo que hay en la CMDB y se enseña el motivo.
      if (cancelado || !detalle?.provider_id) return;
      setSincronizando(true);
      try {
        const res = await api.syncDnsDomain(domain.id, companyId);
        if (cancelado) return;
        const vivo = res.data || {};
        const limpio = { ...vivo };
        delete limpio.records;
        setForm((prev) => ({ ...prev, ...limpio }));
        setBase((prev) => ({ ...prev, ...limpio }));
        if (Array.isArray(vivo.records)) setRecords(vivo.records);
        setAviso(vivo.last_sync_message || 'Zona sincronizada con OVH');
      } catch (err) {
        if (!cancelado) setError(err.message || 'No se pudo leer la zona real de OVH');
      } finally {
        if (!cancelado) setSincronizando(false);
      }
    })();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain?.id, companyId, mode]);

  const guardar = async () => {
    if (!empresaValida) {
      setError('Selecciona una empresa en la barra superior antes de guardar');
      return;
    }
    if (!form.name.trim()) {
      setError('El dominio es obligatorio');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name,
        zone: form.zone,
        provider_id: form.provider_id || null,
        status: form.status,
        registrar: form.registrar,
        expires_at: form.expires_at || null,
        auto_renew: Boolean(form.auto_renew),
        notes: form.notes,
      };
      if (form.id) {
        await api.updateDnsDomain(form.id, payload, companyId);
      } else {
        const res = await api.createDnsDomain(payload, companyId);
        const limpio = { ...res };
        delete limpio.records;
        setForm((prev) => ({ ...prev, ...limpio }));
        setBase({ ...FORM_VACIO, ...limpio });
      }
      setBase({
        ...base,
        ...payload,
        provider_id: payload.provider_id || '',
        expires_at: payload.expires_at || '',
      });
      setEditing(false);
      setAviso('Dominio guardado');
      onSaved?.();
    } catch (err) {
      setError(err.message || 'No se pudo guardar el dominio');
    } finally {
      setSaving(false);
    }
  };

  const descartar = () => {
    setForm({ ...base });
    setError('');
    if (!form.id) onClose?.();
    else setEditing(false);
  };

  const borrarDominio = async () => {
    if (!form.id) return;
    setConfirmDelete(false);
    try {
      await api.deleteDnsDomain(form.id, companyId);
      onSaved?.();
      onDeleted?.();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar el dominio');
    }
  };

  const comprobarDisponibilidad = async () => {
    if (!form.name.trim()) {
      setQuote({ busy: false, result: null, error: 'Escribe el dominio (ejemplo.com)' });
      return;
    }
    if (!form.provider_id) {
      setQuote({ busy: false, result: null, error: 'Elige un proveedor OVH' });
      return;
    }
    setQuote({ busy: true, result: null, error: '' });
    try {
      const res = await api.quoteDnsDomain({
        name: form.name,
        provider_id: Number(form.provider_id),
        duration: duracion,
      });
      setQuote({ busy: false, result: res, error: '' });
    } catch (err) {
      setQuote({ busy: false, result: null, error: err.message || 'No se pudo consultar OVH' });
    }
  };

  const registrarEnOvh = async () => {
    setConfirmRegister(false);
    setRegistering(true);
    setError('');
    try {
      const res = await api.registerDnsDomain({
        name: form.name,
        provider_id: Number(form.provider_id),
        company_id: Number(companyId),
        duration: duracion,
      });
      setOrder(res.order || null);
      const limpio = { ...res.data };
      delete limpio.records;
      setForm((prev) => ({ ...prev, ...limpio }));
      setBase({ ...FORM_VACIO, ...limpio });
      setEditing(false);
      setAviso('Pedido creado en OVH: completa el pago para que se registre');
      onSaved?.();
    } catch (err) {
      setError(err.message || 'No se pudo registrar el dominio en OVH');
    } finally {
      setRegistering(false);
    }
  };

  const guardarRegistro = async () => {
    const problema = validarRegistro(registro || {});
    if (problema) {
      setError(problema);
      return;
    }
    setGuardandoRegistro(true);
    setError('');
    try {
      const payload = {
        record_type: registro.record_type,
        subdomain: registro.subdomain || '@',
        target: registro.target,
        ttl: Number(registro.ttl) || 3600,
        priority: registro.priority === '' ? null : registro.priority,
      };
      const res = registro.id
        ? await api.updateDnsRecord(form.id, registro.id, payload, companyId)
        : await api.createDnsRecord(form.id, payload, companyId);
      setRegistro(null);
      await cargarRegistros(form.id);
      setAviso(res?.warning || 'Registro guardado');
    } catch (err) {
      // Si OVH lo rechaza, el backend NO lo guarda: se recarga para no dejar
      // la sensación de que «se ha guardado» algo que no está en la zona.
      await cargarRegistros(form.id).catch(() => {});
      setError(err.message || 'No se pudo guardar el registro');
    } finally {
      setGuardandoRegistro(false);
    }
  };

  const borrarRegistro = async (fila) => {
    setError('');
    try {
      await api.deleteDnsRecord(form.id, fila.id, companyId);
      await cargarRegistros(form.id);
      setAviso('Registro borrado');
    } catch (err) {
      setError(err.message || 'No se pudo borrar el registro');
    }
  };

  const grupos = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const visibles = !q ? records : records.filter((fila) => (
      (fila.subdomain || '').toLowerCase().includes(q)
      || (fila.target || '').toLowerCase().includes(q)
      || (fila.record_type || '').toLowerCase().includes(q)
    ));
    const porTipo = {};
    visibles.forEach((fila) => {
      const tipo = fila.record_type || 'OTRO';
      (porTipo[tipo] = porTipo[tipo] || []).push(fila);
    });
    const orden = TIPOS.filter((tipo) => porTipo[tipo]?.length);
    Object.keys(porTipo).forEach((tipo) => {
      if (!orden.includes(tipo)) orden.push(tipo);
    });
    return orden.map((tipo) => ({ tipo, filas: porTipo[tipo] }));
  }, [records, busqueda]);

  // Al abrir el alta de un registro se lleva la vista al editor: si la lista es
  // larga, el formulario quedaba fuera de pantalla.
  const nuevoRegistroAbierto = Boolean(registro && !registro.id);
  useEffect(() => {
    if (nuevoRegistroAbierto) editorNuevoRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [nuevoRegistroAbierto]);

  const abrirNuevoRegistro = () => {
    setError('');
    setRegistro({ ...REGISTRO_VACIO });
  };

  const ven = vencimiento(form.expires_at);
  // OVH no tiene tipo ALIAS; con proveedor se oculta para no ofrecer algo que
  // OVH va a rechazar siempre.
  const tiposRegistro = form.provider_id ? TIPOS.filter((t) => t !== 'ALIAS') : TIPOS;
  const badgeEstado = estadoTono(form.status);
  const badges = [
    { text: estadoLabel(form.status), tone: badgeEstado || undefined, color: badgeEstado ? undefined : '#64748b' },
  ];
  if (ven) {
    badges.push({
      text: ven.dias < 0 ? `Caducado hace ${Math.abs(ven.dias)} d` : `Caduca en ${ven.dias} d`,
      tone: ven.tono === 'ok' ? undefined : ven.tono,
      color: ven.tono === 'ok' ? '#16a34a' : undefined,
    });
  }

  const indice = siblings.findIndex((fila) => String(fila.id) === String(form.id));
  const total = siblings.length;

  const irA = (paso) => {
    if (indice < 0) return;
    const destino = siblings[indice + paso];
    if (!destino) return;
    if (dirty && !window.confirm('Tienes cambios sin guardar en este dominio. ¿Descartarlos y pasar al siguiente?')) return;
    onNavigate?.(destino);
  };

  const tabs = [
    { key: 'general', label: 'General' },
    { key: 'registros', label: `Registros (${records.length})` },
  ];

  const campo = (etiqueta, clave, placeholder, opciones = {}) => (
    <div className="sf2-field" style={opciones.full ? { gridColumn: '1 / -1' } : undefined}>
      <label>{etiqueta}</label>
      {editing ? (
        opciones.select ? (
          <select value={form[clave] ?? ''} onChange={(e) => sf(clave, e.target.value)}>
            {opciones.select}
          </select>
        ) : (
          <input
            type={opciones.type || 'text'}
            value={form[clave] ?? ''}
            placeholder={placeholder}
            onChange={(e) => sf(clave, e.target.value)}
          />
        )
      ) : (
        <div className="v">{form[clave] || '—'}</div>
      )}
    </div>
  );

  return (
    <div className="dv-overlay">
      <div className="dv-topbar">
        <button className="dv-back-btn" type="button" onClick={onClose}>
          <ArrowLeftIcon style={{ width: 15, height: 15 }} /> DNS
        </button>
        <DetailCrumb label={form.name || 'Nuevo dominio'} />
        {!esAlta && total > 0 && (
          <div className="dv-pager">
            <button className="dv-pager-btn" type="button" onClick={() => irA(-1)} disabled={indice <= 0}>‹</button>
            <span className="dv-pager-count">{indice >= 0 ? indice + 1 : 1} / {total}</span>
            <button className="dv-pager-btn" type="button" onClick={() => irA(1)} disabled={indice < 0 || indice >= total - 1}>›</button>
          </div>
        )}
        <div className="dv-topbar-spacer" />
        {error && <span style={{ color: '#dc2626', fontSize: '0.78rem', maxWidth: 360 }}>⚠ {error}</span>}
        <div className="dv-topbar-actions">
          {!esAlta && (
            <button
              className="dv-btn dv-btn-secondary"
              type="button"
              onClick={() => sincronizar(false)}
              disabled={sincronizando || !form.provider_id}
              title={form.provider_id ? 'Traer la zona real de OVH' : 'El dominio no tiene proveedor'}
            >
              <ArrowPathIcon style={{ width: 14, height: 14 }} />
              {sincronizando ? 'Sincronizando…' : 'Sincronizar'}
            </button>
          )}
          {!editing ? (
            <>
              {!esAlta && (
                <button className="dv-btn dv-btn-secondary" type="button" onClick={() => setEditing(true)}>
                  <PencilSquareIcon style={{ width: 14, height: 14 }} /> Editar
                </button>
              )}
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon style={{ width: 14, height: 14 }} /> Cerrar
              </button>
            </>
          ) : (
            <>
              {!esAlta && (
                <button
                  className="dv-btn dv-btn-secondary"
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  title="Eliminar el dominio de la CMDB"
                >
                  <TrashIcon style={{ width: 14, height: 14 }} /> Eliminar
                </button>
              )}
              <button className="dv-btn dv-btn-secondary" type="button" onClick={descartar} disabled={saving}>
                <XMarkIcon style={{ width: 14, height: 14 }} /> Descartar
              </button>
              <button
                className="dv-btn dv-btn-primary"
                type="button"
                onClick={guardar}
                disabled={saving || (!esAlta && !dirty)}
                title={dirty ? 'Guardar los cambios' : 'Sin cambios'}
              >
                {saving ? <span className="spinner-border spinner-border-sm" /> : <CheckIcon style={{ width: 14, height: 14 }} />}
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="dv-body">
        <form className={`dv-main${isCards ? ' sf2-main' : ''}`} onSubmit={(e) => e.preventDefault()}>
          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <FormCardsHead
                icon={<GlobeAltIcon style={{ width: 20, height: 20 }} />}
                title={form.name}
                onTitleChange={(valor) => sf('name', valor)}
                placeholder="ejemplo.com"
                editing={editing}
                saving={saving}
                onSave={guardar}
                onCancel={descartar}
                onEdit={() => setEditing(true)}
                crumb={[form.zone && form.zone !== form.name ? form.zone : null, form.provider_name]
                  .filter(Boolean).join(' · ')}
                badges={badges}
                chips={[
                  { value: records.length, label: 'registros' },
                  ...(form.records_synced ? [{ value: form.records_synced, label: 'en OVH' }] : []),
                ]}
                dirty={dirty}
                inline
                titleDirty={dirty}
                onTitleCommit={() => { if (form.id) guardar(); }}
                onTitleReset={() => sf('name', base.name)}
              />
            ) : null}
            tabs={(
              <div className="dv-tabs-nav">
                {tabs.map((t) => (
                  <TabButton
                    key={t.key}
                    type="button"
                    className={`dv-tab-btn${tab === t.key ? ' active' : ''}`}
                    onClick={() => setTab(t.key)}
                  >
                    {t.label}
                  </TabButton>
                ))}
              </div>
            )}
          >
            {tab === 'general' && (
              <div className="dv-tab-pane sf2-pane">
                <fieldset className="dv-fieldset" disabled={!editing}>
                  <div className="sf2-grid sf2-grid--2">
                    <div className="sf2-card">
                      <h3>Identificación</h3>
                      <div className="sf2-fields" style={{ '--n': 2 }}>
                        {campo('Dominio *', 'name', 'ejemplo.com', { full: true })}
                        {campo('Zona en el proveedor', 'zone', '(igual que el dominio)')}
                        <div className="sf2-field">
                          <label>Proveedor</label>
                          {editing ? (
                            <select
                              value={form.provider_id || ''}
                              onChange={(e) => sf('provider_id', e.target.value)}
                            >
                              <option value="">— sin API (solo CMDB) —</option>
                              {providers.map((p) => (
                                <option key={p.id} value={p.id}>{p.name} · {p.provider_label}</option>
                              ))}
                            </select>
                          ) : (
                            <div className="v">{form.provider_name || '— sin API —'}</div>
                          )}
                        </div>
                        {campo('Registrador', 'registrar', 'OVH, GoDaddy…')}
                        <div className="sf2-field">
                          <label>Estado</label>
                          {editing ? (
                            <select value={form.status} onChange={(e) => sf('status', e.target.value)}>
                              {ESTADOS.map((e) => <option key={e.value} value={e.value}>{e.label}</option>)}
                            </select>
                          ) : <div className="v">{estadoLabel(form.status)}</div>}
                        </div>
                      </div>
                    </div>

                    <div className="sf2-col-stack">
                      <div className="sf2-card">
                        <h3>Renovación</h3>
                        <div className="sf2-fields" style={{ '--n': 2 }}>
                          {campo('Caduca el', 'expires_at', '', { type: 'date' })}
                          <div className="sf2-field">
                            <label>Queda</label>
                            {ven ? (
                              <div className="v">
                                <span className={`sf2-pill ${ven.tono === 'bad' ? 'sf2-pill--bad' : ven.tono === 'warn' ? 'sf2-pill--wa' : 'sf2-pill--ok'}`}>
                                  {ven.dias < 0 ? `${Math.abs(ven.dias)} días vencido` : `${ven.dias} días`}
                                </span>
                              </div>
                            ) : <div className="v">—</div>}
                          </div>
                          <div className="sf2-field" style={{ gridColumn: '1 / -1' }}>
                            <label>Renovación automática</label>
                            <label className="sf2-switch sf2-switch--sm">
                              <input
                                type="checkbox"
                                checked={Boolean(form.auto_renew)}
                                onChange={(e) => sf('auto_renew', e.target.checked)}
                                disabled={!editing}
                              />
                              <span className="sf2-switch-track"><span className="sf2-switch-thumb" /></span>
                              <span className="sf2-switch-label">{form.auto_renew ? 'Se renueva sola' : 'Renovación manual'}</span>
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="sf2-card">
                        <h3>OVH</h3>
                        <div className="sf2-fields" style={{ '--n': 1 }}>
                          <div className="sf2-field">
                            <label>Última sincronización</label>
                            <div className="v">
                              {form.last_sync_at ? new Date(form.last_sync_at).toLocaleString() : '—'}
                            </div>
                            {form.last_sync_message && <div className="sf2-hint">{form.last_sync_message}</div>}
                          </div>
                        </div>
                        {aviso && <div className="sf2-hint">{aviso}</div>}
                      </div>
                    </div>

                    <div className="sf2-card" style={{ gridColumn: '1 / -1' }}>
                      <h3>Notas</h3>
                      <div className="sf2-fields" style={{ '--n': 1 }}>
                        <div className="sf2-field">
                          <label>Observaciones</label>
                          {editing ? (
                            <textarea
                              rows={4}
                              value={form.notes || ''}
                              onChange={(e) => sf('notes', e.target.value)}
                              placeholder="Para qué se usa, quién lo paga, incidencias…"
                            />
                          ) : (
                            <div className="v" style={{ whiteSpace: 'pre-wrap' }}>{form.notes || '—'}</div>
                          )}
                        </div>
                      </div>
                    </div>

                    {esAlta && (
                      <>
                        <div className="sf2-card">
                          <h3>Disponibilidad y precio</h3>
                          <p className="sf2-hint" style={{ marginTop: 0 }}>
                            Dar de alta un dominio <strong>tiene coste</strong>: OVH cobra el registro
                            (y la zona si se crea). Aquí se simula el pedido para ver disponibilidad y
                            precio; no se compra nada hasta pulsar «Registrar en OVH».
                          </p>
                          <div className="sf2-fields" style={{ '--n': 2 }}>
                            <div className="sf2-field">
                              <label>Periodo</label>
                              <select value={duracion} onChange={(e) => setDuracion(e.target.value)}>
                                {DURACIONES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                              </select>
                            </div>
                            <div className="sf2-field" style={{ justifyContent: 'flex-end' }}>
                              <label>&nbsp;</label>
                              <button
                                type="button"
                              className="dv-btn dv-btn-secondary"
                                onClick={comprobarDisponibilidad}
                                disabled={quote.busy}
                              >
                                {quote.busy ? 'Consultando OVH…' : 'Comprobar disponibilidad'}
                              </button>
                            </div>
                          </div>

                          {quote.error && <p style={{ color: '#dc2626', fontSize: '0.8rem' }}>{quote.error}</p>}
                          {quote.result && (
                            quote.result.available ? (
                              <div style={{ marginTop: 8 }}>
                                <span className="sf2-pill sf2-pill--ok">Disponible</span>
                                <p className="sf2-hint" style={{ marginBottom: 4 }}>
                                  Precio ({quote.result.duration}):&nbsp;
                                  <strong>{quote.result.pricing?.with_tax || '—'}</strong>
                                  {quote.result.pricing?.without_tax ? ` · sin IVA ${quote.result.pricing.without_tax}` : ''}
                                </p>
                                <button
                                  type="button"
                                  className="dv-btn dv-btn-primary"
                                  onClick={() => setConfirmRegister(true)}
                                  disabled={registering}
                                >
                                  <CloudArrowUpIcon style={{ width: 15, height: 15 }} />
                                  {registering ? 'Creando pedido…' : 'Registrar en OVH'}
                                </button>
                              </div>
                            ) : (
                              <div style={{ marginTop: 8 }}>
                                <span className="sf2-pill sf2-pill--bad">No disponible</span>
                                <p className="sf2-hint" style={{ marginBottom: 0 }}>{quote.result.message}</p>
                              </div>
                            )
                          )}

                          {order && (
                            <div style={{ marginTop: 10 }}>
                              <span className="sf2-pill sf2-pill--wa">Pedido {order.order_id} creado</span>
                              <p className="sf2-hint">
                                Falta el pago en OVH{order.price ? ` (${order.price})` : ''}. En cuanto se
                                cree la zona, pulsa «Sincronizar» para traer registros y caducidad.
                              </p>
                              {order.url && (
                                <a className="dv-btn dv-btn-secondary" href={order.url} target="_blank" rel="noreferrer">
                                  Abrir el pago en OVH
                                </a>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="sf2-card">
                          <h3>Solo en la CMDB</h3>
                          <p className="sf2-hint" style={{ marginTop: 0 }}>
                            Si el dominio ya está registrado (en OVH u otro registrador) y solo
                            quieres inventariarlo, usa «Guardar» y luego «Sincronizar» si la zona
                            está en OVH. No se compra ni se envía nada.
                          </p>
                          <button type="button" className="dv-btn dv-btn-secondary" onClick={guardar} disabled={saving || !empresaValida}>
                            {saving ? 'Guardando…' : 'Guardar sin registrar'}
                          </button>
                          {!empresaValida && (
                            <p className="sf2-hint">Selecciona una empresa en la barra superior.</p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </fieldset>
              </div>
            )}

            {tab === 'registros' && (
              <div className="dv-tab-pane sf2-pane">
                {/* Sin fieldset[disabled]: sus botones quedarían muertos en
                    lectura y aquí se da de alta y se borra también sin editar
                    la ficha (cada cambio va directo a OVH). */}
                <div className="sf2-grid sf2-grid--2">
                    <div className="sf2-card" style={{ gridColumn: '1 / -1' }}>
                      <h3 style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        Registros de la zona
                        {form.provider_id && (
                          <span className="sf2-hint" style={{ margin: 0 }}>
                            con proveedor: se escriben en OVH y se publican
                          </span>
                        )}
                        <span style={{ flex: 1 }} />
                        <span className="sf2-input-row" style={{ width: 280 }}>
                          <MagnifyingGlassIcon style={{ width: 15, height: 15, alignSelf: 'center', color: 'var(--text-muted)', flex: '0 0 auto' }} />
                          <input
                            className="sf2-cell-input"
                            placeholder={`Buscar entre ${records.length} registros…`}
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                          />
                        </span>
                        <button
                          type="button"
                          className="dv-btn dv-btn-primary"
                          onClick={abrirNuevoRegistro}
                        >
                          <PlusIcon style={{ width: 14, height: 14 }} /> Añadir registro
                        </button>
                      </h3>

                      {cargando && records.length === 0 && <p className="sf2-hint">Cargando registros…</p>}
                      {error && (
                        <p style={{ color: 'var(--red-600, #dc2626)', fontSize: '0.8rem', margin: '6px 0 0' }}>
                          ⚠ {error}
                        </p>
                      )}
                      {!cargando && records.length === 0 && !registro && (
                        <p className="sf2-hint">
                          Sin registros. Añade uno o pulsa «Sincronizar» para traer la zona real.
                        </p>
                      )}
                      {!cargando && records.length > 0 && grupos.length === 0 && (
                        <p className="sf2-hint">Ningún registro coincide con «{busqueda}».</p>
                      )}
                    </div>

                    {registro && !registro.id && (
                      <div className="sf2-card" style={{ gridColumn: '1 / -1' }} ref={editorNuevoRef}>
                        <h3>Nuevo registro</h3>
                        <div className="sf2-fields" style={{ '--n': 4 }}>
                          <div className="sf2-field">
                            <label>Tipo</label>
                            <select
                              value={registro.record_type}
                              onChange={(e) => setRegistro((r) => ({ ...r, record_type: e.target.value }))}
                            >
                              {tiposRegistro.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </div>
                          <div className="sf2-field">
                            <label>Nombre</label>
                            <input
                              value={registro.subdomain}
                              placeholder="@ o www"
                              onChange={(e) => setRegistro((r) => ({ ...r, subdomain: e.target.value }))}
                            />
                          </div>
                          <div className="sf2-field" style={{ gridColumn: 'span 2' }}>
                            <label>Destino</label>
                            <input
                              value={registro.target}
                              placeholder={PLACEHOLDER_DESTINO[registro.record_type] || 'Destino del registro'}
                              onChange={(e) => setRegistro((r) => ({ ...r, target: e.target.value }))}
                            />
                          </div>
                          <div className="sf2-field">
                            <label>TTL</label>
                            <input
                              type="number"
                              value={registro.ttl}
                              onChange={(e) => setRegistro((r) => ({ ...r, ttl: e.target.value }))}
                            />
                          </div>
                          {['MX', 'SRV'].includes(registro.record_type) && (
                            <div className="sf2-field">
                              <label>Prioridad</label>
                              <input
                                type="number"
                                value={registro.priority ?? ''}
                                onChange={(e) => setRegistro((r) => ({ ...r, priority: e.target.value }))}
                              />
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
                          <button type="button" className="dv-btn dv-btn-secondary" onClick={() => setRegistro(null)}>
                            Cancelar
                          </button>
                          <button
                            type="button"
                            className="dv-btn dv-btn-primary"
                            onClick={guardarRegistro}
                            disabled={guardandoRegistro}
                          >
                            {guardandoRegistro ? 'Guardando…' : 'Guardar registro'}
                          </button>
                        </div>
                      </div>
                    )}

                    {grupos.map((grupo) => (
                      <div className="sf2-card" key={grupo.tipo} style={{ gridColumn: '1 / -1' }}>
                        <h3>
                          {grupo.tipo}
                          <span className="sf2-hint" style={{ margin: '0 0 0 8px', fontWeight: 400 }}>
                            {grupo.filas.length} {grupo.filas.length === 1 ? 'registro' : 'registros'}
                          </span>
                        </h3>
                        <div className="sf2-table-wrap">
                          <table className="sf2-table">
                            <thead>
                              <tr>
                                <th>Nombre</th>
                                <th>Destino</th>
                                <th>TTL</th>
                                <th>Prio</th>
                                <th>Origen</th>
                                <th />
                              </tr>
                            </thead>
                            <tbody>
                              {grupo.filas.map((fila) => {
                                // Edición EN LA PROPIA LÍNEA: antes el editor salía
                                // arriba y, si la fila estaba abajo, no se veía.
                                if (registro?.id === fila.id) {
                                  return (
                                    <tr key={fila.id}>
                                      <td>
                                        <input
                                          className="sf2-cell-input"
                                          value={registro.subdomain}
                                          aria-label="Nombre del registro"
                                          onChange={(e) => setRegistro((r) => ({ ...r, subdomain: e.target.value }))}
                                        />
                                      </td>
                                      <td>
                                        <input
                                          className="sf2-cell-input"
                                          value={registro.target}
                                          placeholder={PLACEHOLDER_DESTINO[registro.record_type] || 'Destino'}
                                          aria-label="Destino del registro"
                                          onChange={(e) => setRegistro((r) => ({ ...r, target: e.target.value }))}
                                        />
                                      </td>
                                      <td>
                                        <input
                                          className="sf2-cell-input"
                                          type="number"
                                          value={registro.ttl}
                                          aria-label="TTL"
                                          onChange={(e) => setRegistro((r) => ({ ...r, ttl: e.target.value }))}
                                        />
                                      </td>
                                      <td>
                                        <input
                                          className="sf2-cell-input"
                                          type="number"
                                          value={registro.priority ?? ''}
                                          aria-label="Prioridad"
                                          onChange={(e) => setRegistro((r) => ({ ...r, priority: e.target.value }))}
                                        />
                                      </td>
                                      <td>
                                        <select
                                          className="sf2-cell-input"
                                          value={registro.record_type}
                                          aria-label="Tipo de registro"
                                          onChange={(e) => setRegistro((r) => ({ ...r, record_type: e.target.value }))}
                                        >
                                          {tiposRegistro.map((t) => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                      </td>
                                      <td style={{ whiteSpace: 'nowrap' }}>
                                        <button
                                          type="button"
                                          className="sf2-icon-btn"
                                          title="Guardar"
                                          onClick={guardarRegistro}
                                          disabled={guardandoRegistro}
                                        >
                                          <CheckIcon />
                                        </button>
                                        <button
                                          type="button"
                                          className="sf2-icon-btn"
                                          title="Cancelar"
                                          onClick={() => setRegistro(null)}
                                        >
                                          <XMarkIcon />
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                }
                                return (
                                  <tr key={fila.id}>
                                    <td>{fila.subdomain}</td>
                                    <td style={{ wordBreak: 'break-all' }}>{fila.target}</td>
                                    <td>{fila.ttl}</td>
                                    <td>{fila.priority ?? '—'}</td>
                                    <td>
                                      <span className={`sf2-pill ${fila.source === 'provider' ? 'sf2-pill--info' : ''}`}>
                                        {fila.source}
                                      </span>
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                      <button
                                        type="button"
                                        className="sf2-icon-btn"
                                        title="Editar en la línea"
                                        onClick={() => { setError(''); setRegistro({ ...fila }); }}
                                      >
                                        <PencilSquareIcon />
                                      </button>
                                      <button
                                        type="button"
                                        className="sf2-icon-btn"
                                        title="Eliminar"
                                        onClick={() => borrarRegistro(fila)}
                                      >
                                        <TrashIcon />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
              </div>
            )}
            <FormDirtyDock
              show={isCards && dirty}
              saving={saving}
              onDiscard={descartar}
              onSave={guardar}
            />
          </FormCardsShell>
        </form>
      </div>

      {confirmDelete && (
        <Dialog
          title="Eliminar dominio"
          icon={<TrashIcon style={{ width: 18, height: 18 }} />}
          maxWidth={480}
          onClose={() => setConfirmDelete(false)}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setConfirmDelete(false)}>
                Cancelar
              </button>
              <button type="button" className="list-btn-primary" onClick={borrarDominio}>
                Eliminar de la CMDB
              </button>
            </>
          )}
        >
          <p className="list-hint" style={{ marginTop: 0 }}>
            ¿Eliminar «{form.name}» de la CMDB? <strong>No</strong> se toca la zona real del
            proveedor ni se cancela el dominio.
          </p>
        </Dialog>
      )}

      {confirmRegister && (
        <Dialog
          title="Registrar dominio en OVH"
          icon={<CloudArrowUpIcon style={{ width: 18, height: 18 }} />}
          maxWidth={520}
          onClose={() => setConfirmRegister(false)}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setConfirmRegister(false)}>
                Cancelar
              </button>
              <button type="button" className="list-btn-primary" onClick={registrarEnOvh}>
                Crear el pedido
              </button>
            </>
          )}
        >
          <p className="list-hint" style={{ marginTop: 0 }}>
            Se creará un pedido real en OVH para <strong>{form.name}</strong> por{' '}
            <strong>{quote.result?.pricing?.with_tax || 'el precio simulado'}</strong>. OVH no cobra
            hasta que completes el pago en su web; la CMDB no guarda datos de pago.
          </p>
        </Dialog>
      )}
    </div>
  );
}
